import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { Grid, Text, TextInput, useMantineColorScheme } from "@mantine/core";
import { projectDetailsAtom } from "../../../utils/atoms/projectDetailsAtom";
import { useRef, useMemo, useCallback } from "react";
import { helpAtom } from "../../../utils/atoms/helpAtom";
import { responsiveCol } from "../../../constants/helper";

const PVInvestment = ({
  form,
  updatePVData,
  componentDetails,
  getFieldAttributes,
}) => {
  const { t } = useTranslation();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const { colorScheme } = useMantineColorScheme();
  const grayIconStyle = useMemo(
    () => ({
      borderRadius: "4px",
      padding: "4px",
    }),
    [colorScheme]
  );
  const containerRef = useRef(null);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const pvParameters =
    componentDetails?.pv?.economicAttributes?.pvParameters || {};
  const currency = projectDetails?.projectDTO?.currencyCode || "$";
  const isNewSetup =
    componentDetails?.pv?.uiAttributes?.pvParameters?.pvSetup?.toLowerCase() === "new";

  const handleBlur = useCallback(
    (event) => {
      if (!containerRef.current?.contains(event.relatedTarget)) {
        setHelperAtom({
          ...helperAtom,
          isFocused: false,
          helpSection: { title: "", description: "", type: "", other: "" },
        });
      }
    },
    [helperAtom, setHelperAtom]
  );

  const handleFocus = useCallback((event) => {
    const dataId = event.target.getAttribute("data-id");
    if (dataId) {
      const cardElement = document.querySelector(`[card-id="${dataId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        cardElement.style.transition = "background-color 0.5s ease";
        cardElement.style.border = "2px solid #42b4e6";
        setTimeout(() => {
          cardElement.style.border = "";
        }, 2000);
      }
    }
  }, []);

  const renderRightSection = useCallback(
    (text, marginRight) => (
      <div style={{ ...grayIconStyle, marginRight }}>{text}</div>
    ),
    [grayIconStyle]
  );

  const solarPanelFields = useMemo(
    () => [
      //   {
      //   id: "field-12",
      //   label: t("Nominal_AC_power"),
      //   placeholder: t("pv.placeholder.nominalAcPower"),
      //   value: componentDetails?.pv?.componentAttributes?.pvParameters?.powNomRef || "",
      //   inputKey: "componentAttributes.pvParameters.powNomRef",
      //   changeKey: ["componentAttributes", "powNomRef"],
      //   rightText: "kW",
      //   rightMargin: undefined,
      //   disabled: false,
      // },
            ...(isNewSetup
        ? [
            {
              id: "field-15",
              label: t("OtherCosts"),
              // placeholder: t("pv.placeholder.valueInKw", { defaultValue: "Add Value in kW" }),
              value: pvParameters.pvCapexCost || "",
              inputKey: "economicAttributes.pvParameters.pvCapexCost",
              changeKey: ["economicAttributes", "pvCapexCost"],
              rightText: `${currency}/kW`,
              rightMargin: "2rem",
              testId: "pvCapexCost",
            },
          ]
        : []),

      {
        id: "field-14",
        label: t("OMCost_"),
        // placeholder: t("pv.placeholder.valueInKw", { defaultValue: "Add Value in kW" }),
        value: pvParameters.pvOmCost || "",
        inputKey: "economicAttributes.pvParameters.pvOmCost",
        changeKey: ["economicAttributes", "pvOmCost"],
        rightText: `${currency}/kW/Year`,
        rightMargin: "4rem",
        testId: "pvOmCost",
      },
            {
            id: "field-13",
            label: t("Lifetime"),
            // placeholder: t("pv.placeholder.valueInYears"),
            value: pvParameters.pvLifetime || "",
            inputKey: "economicAttributes.pvParameters.pvLifetime",
            changeKey: ["economicAttributes", "pvLifetime"],
            rightText: t("common.years"),
            rightMargin: "1rem",
            testId: "pvLifetime",
          },

    ],
    [t, componentDetails, pvParameters, currency, isNewSetup]
  );

  const inverterFields = useMemo(
    () => [
       ...(isNewSetup
        ? [
            {
              id: "field-18",
              label: t("OtherCosts"),
              placeholder: t("pv.placeholder.valueInKw"),
              value: pvParameters.invCapexCost || "",
              inputKey: "economicAttributes.pvParameters.invCapexCost",
              changeKey: ["economicAttributes", "invCapexCost"],
              rightText: `${currency}/kW`,
              rightMargin: "2rem",
              testId: "invCapexCost",
            },
          ]
        : []),
              {
              id: "field-17",
              label: t("OMCost_"),
              placeholder: t("pv.placeholder.valueInKw"),
              value: pvParameters.invOmCost || "",
              inputKey: "economicAttributes.pvParameters.invOmCost",
              changeKey: ["economicAttributes", "invOmCost"],
              rightText: `${currency}/kW/Year`,
              rightMargin: "4rem",
              testId: "invOmCost",
            },
      {
        id: "field-16",
        label: t("Lifetime"),
        placeholder: t("pv.placeholder.valueInYears"),
        value: pvParameters.invLifetime || "",
        inputKey: "economicAttributes.pvParameters.invLifetime",
        changeKey: ["economicAttributes", "invLifetime"],
        rightText: t("common.years"),
        rightMargin: "1rem",
        testId: "invLifetime",
      },
           


    ],
    [t, pvParameters, currency, isNewSetup]
  );

  const renderField = useCallback(
    (field) => {
      const attributes = getFieldAttributes(field.id);
      const disabled = attributes?.disabled ?? field.disabled;
      const handleFieldChange = (value) => {
        updatePVData(field.changeKey[0], field.changeKey[1], value);

        if (field.changeKey[1] === "pvLifetime") {
          updatePVData("economicAttributes", "invLifetime", value);
        }

        if (field.changeKey[1] === "invLifetime") {
          updatePVData("economicAttributes", "pvLifetime", value);
        }
      };

      return (
        <Grid.Col {...responsiveCol} key={field.id}>
          <TextInput
            {...attributes}
            data-id={field.id}
            style={{ marginBottom: "2%" }}
            label={field.label}
            required
            type="text"
            placeholder={field.placeholder}
            value={field.value}
            {...form.getInputProps(field.inputKey)}
            onChange={(e) => handleFieldChange(e.target.value)}
            data-testid={field.testId}
            rightSection={renderRightSection(field.rightText, field.rightMargin)}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            styles={disabled ? { input: { pointerEvents: "none", opacity: 0.5 } } : undefined}
          />
        </Grid.Col>
      );
    },
    [form, getFieldAttributes, updatePVData, renderRightSection]
  );

  const renderSectionHeader = useCallback(
    (icon, title, span = 3) => (
      <Grid gutter="md" className="mt-2 mb-2" style={{ alignItems: "end" }}>
        <Grid.Col span={span}>
          <se-icon size="large">{icon}</se-icon>
          <Text component="span" fw={700}>
            {title}
          </Text>
        </Grid.Col>
      </Grid>
    ),
    []
  );

  return (
    <div ref={containerRef} onFocus={handleFocus} onBlur={handleBlur}>
      {renderSectionHeader("solar_panel1", t("solar_panel"))}
      <Grid gutter="md" className="mt-2 mb-2">
        {solarPanelFields.map(renderField)}
      </Grid>
      {renderSectionHeader("electricity_dc_ac_converter", t("Inverter"), 4)}
      <Grid gutter="md" className="mt-2 mb-2">
        {inverterFields.map(renderField)}
      </Grid>
    </div>
  );
};
export default PVInvestment;
